import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { getMsalInstance } from "./msalInstance";
import InternalApp from "./pages/InternalApp";
import { ThemeProvider, useTheme } from "./ThemeContext";
import "./styles/internal.global.scss";

export default function InternalRoot() {
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const msal = getMsalInstance();
        if (!cancelled) setInstance(msal);

        await msal.initialize();

        const result = await msal.handleRedirectPromise();
        if (result?.account) {
          msal.setActiveAccount(result.account);
        } else if (!msal.getActiveAccount()) {
          const accounts = msal.getAllAccounts();
          if (accounts.length === 1) {
            msal.setActiveAccount(accounts[0]);
          }
        }

        if (!cancelled) setReady(true);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Internal login config error</div>
        <div style={{ marginBottom: 12 }}>{error}</div>
        <div style={{ opacity: 0.8 }}>
          Expected:
          <pre style={{ marginTop: 6, padding: 12, background: "#f6f8fa", overflowX: "auto" }}>
            REACT_APP_AZURE_AD_CLIENT_ID=&lt;GUID&gt;{"\n"}
            REACT_APP_AZURE_AD_AUTHORITY=https://login.microsoftonline.com/common{"\n"}
            REACT_APP_AZURE_AD_API_SCOPE=api://&lt;API_CLIENT_ID&gt;/access_as_user
          </pre>
          After updating CRA env vars, restart the dev server.
        </div>
      </div>
    );
  }

  if (!ready || !instance) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  return (
    <ThemeProvider>
      <InternalRootInner instance={instance} />
    </ThemeProvider>
  );
}

function InternalRootInner({ instance }: { instance: PublicClientApplication }) {
  const { theme } = useTheme();

  return (
    <div className="internal-root" data-theme={theme}>
      <MsalProvider instance={instance}>
        <Routes>
          <Route path="/*" element={<InternalApp />} />
        </Routes>
      </MsalProvider>
    </div>
  );
}
